import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { ANN_TYPES } from './constants';
import { EditIcon, TrashIcon } from '../../components/ui/Icons';
import ConfirmModal from '../../components/ui/ConfirmModal';
import PanelCard, { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const TONE_CLASSES = {
  sky:   'bg-sky-500/12 text-sky-300 border-sky-500/25',
  brand: 'bg-brand-500/12 text-brand-200 border-brand-400/25',
  amber: 'bg-amber-500/12 text-amber-300 border-amber-500/25',
};

export default function AnnouncementsTab() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', content: '', type: 'announcement' });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);


  const fetchItems = () => api.get('/announcements').then(res => setItems(res.data.announcements));

  useEffect(() => { fetchItems().finally(() => setLoading(false)); }, []);

  const resetForm = () => {
    setForm({ title: '', content: '', type: 'announcement' });
    setEditingId(null);
  };

  const startEdit = (a) => {
    setForm({ title: a.title, content: a.content, type: a.type });
    setEditingId(a.id);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/announcements/${editingId}`, form);
        toast.success('Announcement updated.');
      } else {
        await api.post('/announcements', form);
        toast.success('Announcement published.');
      }
      resetForm();
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted.');
      await fetchItems();
      if (editingId === id) resetForm();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
    <div className="grid md:grid-cols-2 gap-6 items-start">
      <PanelCard title={editingId ? 'Edit Announcement' : 'New Announcement'}>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label">Title</label>
            <input className="input" value={form.title} maxLength={150}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
          </div>
          <div>
            <label className="label">Content</label>
            <textarea className="textarea min-h-[8rem]" value={form.content} maxLength={5000}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))} required />
          </div>
          <div>
            <label className="label">Type</label>
            <div className="grid grid-cols-3 gap-2">
              {ANN_TYPES.map(t => (
                <button type="button" key={t.id}
                  onClick={() => setForm(f => ({ ...f, type: t.id }))}
                  className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border
                              transition-all duration-200 ease-smooth ${
                    form.type === t.id
                      ? 'bg-brand-gradient border-brand-400/25 text-white shadow-glow-sm'
                      : 'border-white/[0.07] bg-white/[0.02] text-gray-400 hover:text-white hover:border-white/[0.16]'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary flex-1 py-2.5" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Publish'}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
            )}
          </div>
        </form>
      </PanelCard>

      <PanelCard flush title="All Announcements" subtitle={`${items.length} total`}>
        {loading ? (
          <div className="py-12 flex justify-center"><LoadingSpinner /></div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<><path d="M3 11v2a1 1 0 001 1h2l5 4V6L6 10H4a1 1 0 00-1 1z" /><path d="M16 9a4 4 0 010 6" /></>}
            title="No announcements yet."
          />
        ) : (
          <div className="divide-y divide-white/[0.05] max-h-[600px] overflow-y-auto">
            {items.map(a => {
              const t = ANN_TYPES.find(x => x.id === a.type) || ANN_TYPES[0];
              return (
                <div key={a.id} className="px-5 py-4 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5
                                          rounded border ${TONE_CLASSES[t.tone] || TONE_CLASSES.sky}`}>
                          {t.label}
                        </span>
                        <span className="text-xs text-gray-600 font-mono">{a.created_at?.slice(0, 10)}</span>
                      </div>
                      <h3 className="font-display font-semibold text-white truncate">{a.title}</h3>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2 leading-relaxed">{a.content}</p>
                      <p className="text-xs text-gray-700 mt-1.5">by {a.created_by_name || 'system'}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => startEdit(a)} title="Edit"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                   text-gray-500 hover:text-white hover:bg-white/[0.07] transition-colors">
                        <EditIcon className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setConfirmDeleteId(a.id)} disabled={deletingId === a.id} title="Delete"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                   text-gray-500 hover:text-red-300 hover:bg-red-500/10
                                   transition-colors disabled:opacity-40">
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PanelCard>
    </div>

    {confirmDeleteId && (
      <ConfirmModal
        title="Delete announcement?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={() => remove(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
      />
    )}
    </>
  );
}
